import clsx from "clsx";
import {
  useCustomerEmailTimeline,
  type EmailTimelineEntry,
} from "../../../helpers/hooks/use-customer-email-timeline";

interface Props {
  customerId: string;
}

const EMAIL_LABELS: Record<string, string> = {
  onboarding_started: "Onboarding —1: Welcome",
  onboarding_complete: "Onboarding 0: Setup Complete",
  ads_live: "Email 1: Ads Live",
  pricing_go_live: "Email 2: Pricing Go-Live",
  week_1: "Email 3: Week 1 Momentum",
  week_2: "Email 4: Week 2 Momentum",
  first_booking: "Email 5: First Booking",
  monthly_review: "Email 6: Monthly Review",
};

const STATUS_BADGE: Record<
  EmailTimelineEntry["status"],
  { label: string; cls: string }
> = {
  sent: { label: "Sent", cls: "bg-gray-100 text-gray-700" },
  delivered: { label: "Delivered", cls: "bg-blue-100 text-blue-800" },
  opened: { label: "Opened", cls: "bg-emerald-100 text-emerald-800" },
  clicked: { label: "Clicked", cls: "bg-emerald-200 text-emerald-900" },
  bounced: { label: "Bounced", cls: "bg-rose-100 text-rose-800" },
  complaint: { label: "Spam Complaint", cls: "bg-amber-100 text-amber-900" },
  rejected: { label: "Rejected", cls: "bg-rose-200 text-rose-900" },
};

function relativeShort(iso: string | null): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return "now";
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

function StatusBadge({ status }: { status: EmailTimelineEntry["status"] }) {
  const meta = STATUS_BADGE[status];
  return (
    <span
      className={clsx(
        "text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider",
        meta.cls,
      )}
    >
      {meta.label}
    </span>
  );
}

function EmailRow({ entry }: { entry: EmailTimelineEntry }) {
  const label = EMAIL_LABELS[entry.emailType] ?? entry.emailType;
  const detail = buildDetail(entry);

  return (
    <div className="flex items-start justify-between gap-4 px-4 py-3 border-b border-border-secondary last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-sm font-medium text-content-primary truncate">
            {label}
          </span>
          <StatusBadge status={entry.status} />
        </div>
        <div className="text-xs text-content-tertiary">
          Sent {relativeShort(entry.sentAt)}
          {detail && <span className="ml-2">· {detail}</span>}
        </div>
      </div>
    </div>
  );
}

function buildDetail(entry: EmailTimelineEntry): string {
  const parts: string[] = [];
  if (entry.openCount > 0) {
    parts.push(
      `Opened ×${entry.openCount}${entry.lastOpenedAt ? ` (last ${relativeShort(entry.lastOpenedAt)})` : ""}`,
    );
  }
  if (entry.clickCount > 0) {
    parts.push(`Clicked ×${entry.clickCount}`);
  }
  if (entry.bouncedAt) {
    const subtype = [entry.bounceType, entry.bounceSubType]
      .filter(Boolean)
      .join("/");
    parts.push(subtype ? `Bounce: ${subtype}` : "Bounced");
  }
  if (entry.complainedAt) {
    parts.push("Marked as spam");
  }
  if (entry.rejectedAt) {
    parts.push(
      `Rejected${entry.rejectReason ? `: ${entry.rejectReason}` : ""}`,
    );
  }
  return parts.join(" · ");
}

/**
 * 360 page: per-customer automated-email engagement.
 *
 * One row per automated email sent to the customer. Status reflects the
 * highest-priority terminal state (rejected > bounced > complaint >
 * clicked > opened > delivered > sent).
 *
 * Empty when no automated emails have been sent — common for very new
 * customers or customers added before automation was wired up.
 */
export function EmailTimelineSection({ customerId }: Props) {
  const { data, isLoading, isError, error, refetch, isFetching } =
    useCustomerEmailTimeline(customerId);

  return (
    <section className="bg-white rounded-xl border border-border-secondary shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-secondary">
        <h2 className="text-sm font-semibold text-content-primary">
          Automated Email Engagement
        </h2>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="text-xs font-medium text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isFetching ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {isLoading && (
        <div className="p-4 text-xs text-content-tertiary italic">
          Loading…
        </div>
      )}

      {isError && (
        <div className="p-4 text-xs text-rose-700">
          Failed to load: {(error as Error)?.message ?? "unknown error"}
        </div>
      )}

      {!isLoading && !isError && (data?.length ?? 0) === 0 && (
        <div className="p-4 text-xs text-content-tertiary italic">
          No automated emails have been sent to this customer yet.
        </div>
      )}

      {!isLoading && !isError && (data?.length ?? 0) > 0 && (
        <div>
          {data!.map((entry) => (
            <EmailRow key={entry.id} entry={entry} />
          ))}
        </div>
      )}
    </section>
  );
}
